import React from "react";
import { AlertCircle, RotateCcw } from "lucide-react";
import styles from "./ChartErrorBoundary.module.css";

export interface ChartErrorBoundaryProps {
  children?: React.ReactNode;
  fallback?: (error: Error | null, reset: () => void) => React.ReactNode;
  chartName?: string;
}

interface ChartErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorCount: number;
}

class ChartErrorBoundary extends React.Component<ChartErrorBoundaryProps, ChartErrorBoundaryState> {
  constructor(props: ChartErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(): Partial<ChartErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState((prevState) => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.error("Chart Error:", error, errorInfo);
    }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    });
  };

  render(): React.ReactNode {
    const { hasError, error, errorCount } = this.state;
    const { children, fallback, chartName = "Chart" } = this.props;

    if (hasError) {
      const isPersistent = errorCount > 2;

      if (fallback) {
        return fallback(error, this.handleReset);
      }

      return (
        <div className={styles.errorContainer} role="alert">
          <div className={styles.errorContent}>
            <AlertCircle className={styles.errorIcon} />
            <div className={styles.errorDetails}>
              <h3 className={styles.errorTitle}>{chartName} Rendering Error</h3>
              <p className={styles.errorMessage}>
                {isPersistent
                  ? "This chart has encountered a persistent error. Please refresh the page."
                  : "An error occurred while rendering this chart."}
              </p>

              {process.env.NODE_ENV !== "production" && error && (
                <details className={styles.errorDetailsDropdown}>
                  <summary className={styles.errorDetailsSummary}>Error details</summary>
                  <pre className={styles.errorDetailsContent}>{error.toString()}</pre>
                </details>
              )}

              <div className={styles.errorActions}>
                {!isPersistent && (
                  <button onClick={this.handleReset} className={styles.retryButton}>
                    <RotateCcw className={styles.buttonIcon} />
                    Try again
                  </button>
                )}
                <button onClick={() => window.location.reload()} className={styles.refreshButton}>
                  Refresh page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ChartErrorBoundary;
